import { deriveAgreementSignatory } from '@statura/utils/representative';
import { isSahMeansBasisComplete } from '@statura/utils/sah';
import type { SahPanelModel } from '@/components/clients/useSahBudgetModel';

const DELIVERY_ASSESSMENT_TYPES = new Set([
  'ACAT',
  'COMPREHENSIVE_CARE',
  'FUNCTIONAL',
  'IAT',
  'INITIAL',
  'NSAF',
  'REASSESSMENT',
  'REVIEW',
  'SUPPORT_NEEDS',
]);

export function hasCompletedProviderAssessment(
  assessments: Array<{
    assessment_type?: string | null;
    status?: string | null;
    completed_at?: string | null;
  }>,
): boolean {
  return assessments.some((assessment) => {
    const status = String(assessment.status || '').toUpperCase();
    const kind = String(assessment.assessment_type || '').toUpperCase();
    return (
      DELIVERY_ASSESSMENT_TYPES.has(kind) &&
      (status === 'COMPLETED' || status === 'SIGNED_OFF' || Boolean(assessment.completed_at))
    );
  });
}

export function providerDeliveryReadiness(data: SahPanelModel): {
  ready: boolean;
  blockers: string[];
} {
  const hasClassification =
    data.classifications.length > 0 ||
    data.classificationLevel != null ||
    data.transitionedHcpLevel != null;
  const hasAssessment = hasCompletedProviderAssessment(data.assessments);
  const supportPlan = data.currentSupportPlanId
    ? data.carePlans.find((plan) => plan.id === data.currentSupportPlanId)
    : null;
  const authority = deriveAgreementSignatory({
    capacity: data.capacity,
    participantName: data.participantName,
    decisionMakers: data.decisionMakers.filter((decisionMaker) => decisionMaker.isActive),
  });
  const hasAuthority =
    authority.kind === 'PARTICIPANT' ||
    (authority.kind === 'SUBSTITUTE_DECISION_MAKER' && authority.contactLinked);

  const blockers = [
    !data.serviceAreaId ? 'service area' : null,
    !hasClassification ? 'funding classification' : null,
    !isSahMeansBasisComplete(
      data.means.dbTier,
      data.means.assessedIndependence,
      data.means.assessedEverydayLiving,
    )
      ? 'contribution basis'
      : null,
    !hasAuthority ? 'agreement authority' : null,
    !hasAssessment ? 'completed provider assessment' : null,
    String(supportPlan?.status || '').toUpperCase() !== 'ACTIVE' ? 'active care plan' : null,
    !data.hasCurrentServiceInclusions ? 'current service inclusions' : null,
    !data.currentBudget ? 'current-quarter budget' : null,
    !data.hasSignedAgreement ? 'signed service agreement' : null,
    !data.deliveryReady ? 'controlled delivery checks' : null,
  ].filter((value): value is string => Boolean(value));

  return { ready: blockers.length === 0, blockers };
}
